import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { ITileset } from '~/client/graph'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import MapViewItemsSetupStore from './MapViewItemsSetup.store'

enum AcceptedVectorFileTypes {
  zip = 'zip',
  geojson = 'geojson',
  geojsonld = 'geojsonld',
  ldgeojson = 'ldgeojson',
  geojsonl = 'geojsonl',
  ndgeojson = 'ndgeojson',
}

enum AcceptedTiffFileTypes {
  tif = 'tif',
  tiff = 'tiff',
}

export default class TilesetsSetupStore {
  @observable public selectedTilesetId: string = null
  @observable public deletableTileset: ITileset = null
  @observable public isDeletingTileset: boolean = false

  public constructor(
    private readonly tilesetsStore: TilesetsStore,
    private readonly state: InitialState,
    private readonly mapViewItemsSetupStore: MapViewItemsSetupStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get selectedTileset(): ITileset {
    return this.state.activeTilesetsById.get(this.selectedTilesetId)
  }

  @action.bound
  public showDeleteDialog(): void {
    this.showDeleteConfirmationDialogByTileset(this.selectedTileset)
  }

  @action.bound
  public async selectTilesetById(
    id: string,
    shouldSaveSelectedItem: boolean = true,
  ): Promise<void> {
    const tileset = this.state.activeTilesetsById.get(id)
    this.selectTileset(tileset, shouldSaveSelectedItem)
  }

  public selectTileset = async (
    tileset: ITileset,
    shouldSaveSelectedItem: boolean = true,
  ): Promise<void> => {
    if (!tileset) {
      return
    }

    if (shouldSaveSelectedItem) {
      await this.mapViewItemsSetupStore.saveSelectedMapViewItem()
    }

    this.deselectTileset()
    runInAction(() => {
      this.selectedTilesetId = tileset.id
    })
  }

  @action.bound
  public deselectTileset(): void {
    this.selectedTilesetId = null
  }

  @action.bound
  public showDeleteConfirmationDialogByTileset(tileset: ITileset): void {
    this.deletableTileset = tileset
  }

  @action.bound
  public showDeleteConfirmationDialogByTilesetId(tilesetId: string): void {
    this.deletableTileset = this.state.activeTilesetsList.find(
      s => s.id === tilesetId,
    )
  }

  @action.bound
  public hideTilesetDeleteConfirmDialog(): void {
    this.deletableTileset = null
  }

  @action.bound
  public async applyTilesetDeleteConfirmDialog(): Promise<void> {
    if (!this.deletableTileset) {
      return
    }

    this.isDeletingTileset = true
    this.deselectTileset()
    this.tilesetsStore.removeOne(
      this.deletableTileset.id,
      () => {
        runInAction(() => {
          this.hideTilesetDeleteConfirmDialog()
          this.isDeletingTileset = false
        })
      },
      () => {
        runInAction(() => {
          this.isDeletingTileset = false
        })
      },
    )
  }

  public uploadGeoTIFFToMapbox(file: File): void {
    const splittedName = file.name.split('.')
    if (splittedName?.length <= 1) {
      return
    }
    const extention = splittedName.at(-1)
    if (
      Object.values(AcceptedVectorFileTypes).includes(
        extention as AcceptedVectorFileTypes,
      )
    ) {
      // Fire and forget - progress tracked in TilesetUploadProgressStore
      this.tilesetsStore.uploadTileset(file, file.name)
    }
    if (
      Object.values(AcceptedTiffFileTypes).includes(
        extention as AcceptedTiffFileTypes,
      )
    ) {
      // Fire and forget - progress tracked in TilesetUploadProgressStore
      this.tilesetsStore.uploadGeoTIFF(file, file.name)
    }
  }
}
